import React from "react";
import { useSelector } from "react-redux";

import { Impulse, useScoped } from "~/tools/impulse";
import { usePermanent } from "~/tools/use-permanent";

import { selectTimeRange } from "../../../app/state/view/timeline/TimelineSelectors";
import { useNavigateFrom } from "../../../components/react/link";

import { DashboardContextProvider, type DashboardContextValue } from "./_dashboard-context";
import { DashboardState } from "./_dashboard-state";
import { useDashboardVariables } from "./_use-dashboard-variables";
import { DashboardVariablesBoundary } from "./variables-boundary";

export const DashboardProvider: React.FC<{
	children: React.ReactNode;
}> = ({ children }) => {
	const navigateFrom = useNavigateFrom();

	const timeRangeFromUrl = useSelector(selectTimeRange);

	const state = usePermanent(() => new DashboardState());

	const timeRange = React.useMemo(
		() =>
			Impulse(
				(scope) => state.form.fields.settings.fields.timeRange.getInput(scope) ?? timeRangeFromUrl,
				(next, scope) => {
					if (state.form.fields.settings.fields.timeRange.getInput(scope) != null) {
						state.form.fields.settings.fields.timeRange.setInput(next);
					} else {
						navigateFrom("*", {
							search: (params) => ({ ...params, timeRange: next }),
						});
					}
				},
				{
					compare: (left, right) => left.isEqual(right),
				},
			),
		[navigateFrom, state, timeRangeFromUrl],
	);

	const [variables, onVariablesChange] = useDashboardVariables(state);

	const context = React.useMemo(
		(): DashboardContextValue => ({
			timeRange,
			state,
			variables: Impulse(() => variables, onVariablesChange),
		}),
		[timeRange, state, variables, onVariablesChange],
	);

	return (
		<DashboardContextProvider value={context}>
			<DashboardVariablesBoundary timeRange={useScoped(timeRange)} variables={variables}>
				{children}
			</DashboardVariablesBoundary>
		</DashboardContextProvider>
	);
};
